import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FinancialMetric } from '@/types/financial.types';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

interface FinancialSummaryCardProps {
  title: string;
  metrics: FinancialMetric[];
  columns?: 2 | 3 | 4;
}

const highlightClasses = {
  success: 'border-green-500/50 bg-green-500/5',
  warning: 'border-orange-500/50 bg-orange-500/5',
  danger: 'border-red-500/50 bg-red-500/5',
  info: 'border-blue-500/50 bg-blue-500/5',
};

const textClasses = {
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-orange-600 dark:text-orange-400',
  danger: 'text-red-600 dark:text-red-400',
  info: 'text-blue-600 dark:text-blue-400',
};

const iconClasses = {
  success: 'text-green-600',
  warning: 'text-orange-600',
  danger: 'text-red-600',
  info: 'text-blue-600',
};

export function FinancialSummaryCard({
  title,
  metrics,
  columns = 3,
}: FinancialSummaryCardProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <Card dir="rtl">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn('grid gap-3', gridCols[columns])}>
          {metrics.map((metric, index) => (
            <Card
              key={index}
              className={cn(
                'transition-colors',
                metric.highlight && highlightClasses[metric.highlight]
              )}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  {metric.icon && (
                    <span
                      className={cn(
                        'text-muted-foreground',
                        metric.highlight && iconClasses[metric.highlight]
                      )}
                    >
                      {metric.icon}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">{metric.label}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span
                    className={cn(
                      'text-lg font-bold',
                      metric.highlight && textClasses[metric.highlight]
                    )}
                  >
                    {typeof metric.value === 'number'
                      ? formatCurrency(metric.value)
                      : metric.value}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
